import React, { useState, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useTheme } from '../contexts/ThemeContext';
import { useBreadcrumb } from '../contexts/BreadcrumbContext';
import {
  BookOpen,
  ChevronRight,
  Search,
  MessageCircle,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { btn } from '../utils/buttonStyles';
import { slugify } from '../utils/slugify';

// `division` groups the sidebar into the two halves of the help guide. Every
// entry keeps an id, so the scroll-spy below needs no special-casing.
const SECTIONS = [
  { id: 'getting-started', label: 'Getting Started', division: 'Kynaix Sports' },
  { id: 'your-profile', label: 'Your Profile', division: 'Kynaix Sports' },
  { id: 'squads', label: 'Squads', division: 'Kynaix Sports' },
  { id: 'teams', label: 'Teams', division: 'Kynaix Sports' },
  { id: 'tournaments', label: 'Tournaments', division: 'Kynaix Sports' },
  { id: 'tournament-formats', label: 'Tournament Formats', division: 'Kynaix Sports' },
  { id: 'recording-scores', label: 'Recording Scores', division: 'Kynaix Sports' },
  { id: 'my-games', label: 'My Games', division: 'Kynaix Sports' },
  { id: 'teammates', label: 'Teammates', division: 'Kynaix Sports' },
  { id: 'messaging', label: 'Messaging', division: 'Kynaix Sports' },
  { id: 'social-feed', label: 'Social Feed', division: 'Kynaix Sports' },
  { id: 'marketplace', label: 'Marketplace', division: 'Kynaix Sports' },
  { id: 'academies', label: 'About Academies', division: 'Academies' },
  { id: 'finding-and-joining-an-academy', label: 'Finding & Joining', division: 'Academies' },
  { id: 'academy-programs', label: 'Academy Programs', division: 'Academies' },
  { id: 'open-play-and-meetups', label: 'Open Play & Meetups', division: 'Academies' },
  { id: 'booking-a-court', label: 'Booking a Court', division: 'Academies' },
  { id: 'paying-an-academy', label: 'Paying an Academy', division: 'Academies' },
  { id: 'cancelling-refunds-and-credit', label: 'Cancelling & Refunds', division: 'Academies' },
  { id: 'managed-players', label: 'Managed Players', division: 'Academies' },
  { id: 'creating-an-academy', label: 'Creating an Academy', division: 'Academies' },
  { id: 'academy-settings', label: 'Academy Settings', division: 'Academies' },
  { id: 'academy-features', label: 'Academy Features', division: 'Academies' },
  { id: 'academy-staff-and-access-rights', label: 'Staff & Access Rights', division: 'Academies' },
  { id: 'academy-members', label: 'Academy Members', division: 'Academies' },
  { id: 'running-programs-open-play-and-courts', label: 'Running What You Offer', division: 'Academies' },
  { id: 'prices-and-price-tags', label: 'Prices & Price Tags', division: 'Academies' },
  { id: 'the-kynaix-service-fee', label: 'The Service Fee', division: 'Academies' },
  { id: 'front-desk', label: 'Front Desk', division: 'Academies' },
  { id: 'academy-reports', label: 'Academy Reports', division: 'Academies' },
  { id: 'getting-paid', label: 'Getting Paid', division: 'Academies' },
  { id: 'publishing-your-academy', label: 'Publishing to the Web', division: 'Academies' },
  { id: 'security-checks-and-verification-codes', label: 'Security Checks', division: 'Academies' },
  { id: 'academy-messaging', label: 'Academy Messaging', division: 'Academies' },
];

function makeComponents(theme) {
  return {
    h1: ({ children }) => (
      <h1 className={`text-3xl font-bold ${theme.text} mt-10 mb-4`}>{children}</h1>
    ),
    h2: ({ children }) => (
      <h2
        id={slugify(children)}
        className={`scroll-mt-36 text-2xl font-bold ${theme.text} mt-10 mb-3 pb-2 border-b ${theme.border}`}
      >
        {children}
      </h2>
    ),
    h3: ({ children }) => (
      <h3 id={slugify(children)} className={`scroll-mt-36 text-lg font-semibold ${theme.text} mt-6 mb-2`}>
        {children}
      </h3>
    ),
    p: ({ children }) => (
      <p className={`${theme.textSecondary} leading-relaxed mb-4`}>{children}</p>
    ),
    ul: ({ children }) => (
      <ul className={`list-disc list-inside ${theme.textSecondary} space-y-1 mb-4 ml-2`}>
        {children}
      </ul>
    ),
    ol: ({ children }) => (
      <ol className={`list-decimal list-inside ${theme.textSecondary} space-y-2 mb-4 ml-2`}>
        {children}
      </ol>
    ),
    li: ({ children }) => <li className="leading-relaxed">{children}</li>,
    blockquote: ({ children }) => (
      <blockquote className={`border-l-4 pl-4 py-2 my-4 rounded-r ${theme.tabActive}`}>
        {children}
      </blockquote>
    ),
    table: ({ children }) => (
      <div className="overflow-x-auto mb-6">
        <table className={`w-full text-sm border-collapse border ${theme.border} rounded-lg`}>
          {children}
        </table>
      </div>
    ),
    thead: ({ children }) => <thead className={`${theme.tabInactive}`}>{children}</thead>,
    th: ({ children }) => (
      <th className={`px-4 py-3 text-left border-b ${theme.border} font-semibold ${theme.text}`}>
        {children}
      </th>
    ),
    td: ({ children }) => (
      <td className={`px-4 py-3 border-b ${theme.border} ${theme.textSecondary}`}>{children}</td>
    ),
    tr: ({ children }) => (
      <tr className={`${theme.cardHover} transition-colors`}>{children}</tr>
    ),
    code: ({ inline, children }) =>
      inline ? (
        <code className={`px-1.5 py-0.5 rounded text-sm font-mono ${theme.card} ${theme.text} border ${theme.border}`}>
          {children}
        </code>
      ) : (
        <pre className={`${theme.card} border ${theme.border} rounded-lg p-4 overflow-x-auto mb-4 text-sm font-mono ${theme.textSecondary}`}>
          <code>{children}</code>
        </pre>
      ),
    hr: () => <hr className={`my-8 border-t ${theme.border}`} />,
    strong: ({ children }) => (
      <strong className={`font-semibold ${theme.text}`}>{children}</strong>
    ),
    a: ({ href, children }) => (
      <a
        href={href}
        className={`${theme.link} ${theme.linkHover} underline`}
        target={href?.startsWith('http') ? '_blank' : undefined}
        rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
      >
        {children}
      </a>
    ),
  };
}

export default function HelpPage() {
  const { theme } = useTheme();
  const { setBreadcrumbItems } = useBreadcrumb();
  const [markdown, setMarkdown] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeSection, setActiveSection] = useState('getting-started');
  const [mobileTocOpen, setMobileTocOpen] = useState(false);
  const contentRef = useRef(null);

  useEffect(() => {
    setBreadcrumbItems([{ label: 'Help Center', path: '/help' }]);
    return () => setBreadcrumbItems([]);
  }, [setBreadcrumbItems]);

  // Fetch help.md from the public folder
  useEffect(() => {
    fetch('/help.md')
      .then((res) => {
        if (!res.ok) throw new Error('Could not load help content.');
        return res.text();
      })
      .then((text) => { setMarkdown(text); setLoading(false); })
      .catch((err) => { setError(err.message); setLoading(false); });
  }, []);

  // Deep-link support: markdown loads async, so headings don't exist yet at
  // the browser's own initial hash-scroll attempt — do it ourselves once the
  // content (and therefore the target id) actually exists in the DOM.
  useEffect(() => {
    if (loading || error || !window.location.hash) return;
    // The existing scroll listener (below) picks up the active TOC section
    // from here on — this effect only needs to get the viewport there.
    document.getElementById(window.location.hash.slice(1))?.scrollIntoView();
  }, [loading, error]);

  // Sync active TOC highlight — threshold must match scroll-mt-36 (144px)
  useEffect(() => {
    const handleScroll = () => {
      for (let i = SECTIONS.length - 1; i >= 0; i--) {
        const el = document.getElementById(SECTIONS[i].id);
        if (el && el.getBoundingClientRect().top <= 160) {
          setActiveSection(SECTIONS[i].id);
          return;
        }
      }
      setActiveSection(SECTIONS[0].id);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Filter sections by search query
  const displayedMarkdown = searchQuery.trim()
    ? markdown
        .split('\n## ')
        .filter((s) => s.toLowerCase().includes(searchQuery.toLowerCase()))
        .map((s, i) => (i === 0 ? s : `## ${s}`))
        .join('\n')
    : markdown;

  const components = makeComponents(theme);

  const TocLink = ({ id, label, mobile = false }) => (
    <a
      href={`#${id}`}
      onClick={() => { setActiveSection(id); setMobileTocOpen(false); }}
      className={`flex items-center gap-2 px-2 py-1.5 rounded-lg text-sm transition-colors no-underline ${
        !mobile && activeSection === id
          ? `${theme.buttonPrimary} font-medium`
          : `${theme.textSecondary} ${theme.cardHover}`
      }`}
    >
      <ChevronRight size={14} className="flex-shrink-0" />
      {label}
    </a>
  );

  // One list, split by division — a labelled heading appears wherever the
  // division changes, so the two halves of the guide read as separate.
  const TocList = ({ mobile = false }) =>
    SECTIONS.map((s, i) => (
      <React.Fragment key={s.id}>
        {SECTIONS[i - 1]?.division !== s.division && (
          <p
            className={`text-xs font-bold uppercase tracking-wider ${theme.text} px-2 pb-1.5 ${
              i === 0 ? '' : `mt-4 pt-3 border-t ${theme.border}`
            }`}
          >
            {s.division}
          </p>
        )}
        <TocLink id={s.id} label={s.label} mobile={mobile} />
      </React.Fragment>
    ));

  return (
    <div className={`min-h-screen ${theme.background}`}>

      {/* Compact header bar */}
      <div className={`${theme.background} py-4`}>
        <div className="max-w-7xl mx-auto px-4 lg:px-0">
          <div className={`${theme.card} border ${theme.border} rounded-xl px-6 py-3 flex flex-col sm:flex-row items-center justify-between gap-3 ${theme.shadow}`}>
            <div className="flex items-center gap-3">
              <BookOpen size={22} className={theme.link} />
              <div>
                <p className={`text-lg font-bold leading-tight ${theme.text}`}>Help Center</p>
                <p className={`text-xs leading-tight hidden sm:block ${theme.textSecondary}`}>
                  Everything you need to know about using Kynaix Sports
                </p>
              </div>
            </div>
            <div className="relative w-full sm:w-72">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search the help guide…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={`w-full pl-9 pr-4 py-2 rounded-lg border ${theme.input} focus:ring-2 focus:ring-blue-400 focus:outline-none text-sm`}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 lg:px-0 py-6 flex gap-8">

        {/* Sidebar TOC — desktop */}
        <aside className="hidden lg:block w-56 flex-shrink-0">
          <div
            className={`sticky ${theme.card} border ${theme.border} rounded-xl p-4 shadow-sm`}
            style={{ top: '6rem' }}
          >
            <nav className="space-y-0.5 max-h-[calc(100vh-9rem)] overflow-y-auto">
              <TocList />
            </nav>
          </div>
        </aside>

        {/* Main content */}
        <div className="flex-1 min-w-0">

          {/* Mobile TOC toggle */}
          <div className="lg:hidden mb-4">
            <button
              onClick={() => setMobileTocOpen((o) => !o)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border ${theme.border} ${theme.card} ${theme.text} text-sm font-medium`}
            >
              <span className="flex items-center gap-2">
                <BookOpen size={16} />
                Jump to section
              </span>
              {mobileTocOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            </button>
            {mobileTocOpen && (
              <div className={`mt-1 rounded-xl border ${theme.border} ${theme.card} p-3 space-y-0.5 max-h-[70vh] overflow-y-auto`}>
                <TocList mobile />
              </div>
            )}
          </div>

          {/* Rendered help content */}
          <div
            ref={contentRef}
            className={`${theme.card} border ${theme.border} rounded-xl p-6 md:p-8 shadow-sm`}
          >
            {loading && (
              <div className={`text-center py-20 ${theme.textSecondary}`}>Loading help content…</div>
            )}
            {error && (
              <div className="text-center py-20 text-red-500">{error}</div>
            )}
            {!loading && !error && (
              searchQuery.trim() && displayedMarkdown === '' ? (
                <div className={`text-center py-20 ${theme.textSecondary}`}>
                  No results for "<strong>{searchQuery}</strong>"
                </div>
              ) : (
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{displayedMarkdown}</ReactMarkdown>
              )
            )}
          </div>

          {/* Still need help */}
          <div className={`mt-6 ${theme.card} border ${theme.border} rounded-xl p-6 flex flex-col sm:flex-row items-center justify-between gap-4`}>
            <div>
              <p className={`font-semibold ${theme.text}`}>Still need help?</p>
              <p className={`text-sm ${theme.textSecondary}`}>
                Use the chat assistant — it can answer questions, look up your data, and report issues to our team.
              </p>
            </div>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('show-help-chat'))}
              className={`flex items-center gap-2 flex-shrink-0 ${btn.base} ${btn.md} ${theme.buttonPrimary}`}
            >
              <MessageCircle size={16} />
              Chat with Kyna
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
